import { Injectable } from '@angular/core'
import { Action, Selector, State, StateContext } from '@ngxs/store'
import { tap } from 'rxjs/operators'
import { ReportsStateModel } from './reports.model'
import { ReportsService } from '../reports.service'
import {
  GetCallsReport,
  GetCompareReport,
  GetMonthlyArMovingAverageReport,
  GetMonthlyCategoryMovingAverageReport,
  GetMonthlyObjectivesRankingReport,
  GetSalesReport,
  GetWeeklyArMovingAverageReport,
  GetWeeklyCategoryMovingAverageReport,
  GetWeeklyEmployeeEfficiencyRankingReport,
  GetWeeklyObjectivesRankingReport,
  GetMonthlyEmployeeEfficiencyRankingReport,
  GetUpsellReport,
  GetUpsellList,
  GetDailyAverageReport,
  GetCompareObjectivesByGroup,
  GetCompareObjectivesByRegional,
  GetAdjustedGP,
  ClearAdjustedGP,
  GetTiresReport
} from './reports.actions'
import { ISalesReportDetails } from '../../interfaces/sales-report.interface'
import { ICompareObjectivesByGroup } from '../../interfaces/compare-objectives-by-group.interface'
import { ICompareObjectivesByRegional } from '../../interfaces/compare-objectives-by-regional.interface'
import { sortBy } from 'lodash'
import { IAdjustedGPInfo, IAdjustedGPMonth } from '../../interfaces/adjusted-gross-profit.interface'

@State<ReportsStateModel>({
  name: 'reports'
})
@Injectable()
export class ReportsState {
  constructor(private reportsService: ReportsService) {}

  @Selector()
  static callsReports(state: ReportsStateModel) {
    return state.callsReports
  }

  @Selector()
  static salesReportsDetails(state: ReportsStateModel): ISalesReportDetails[] {
    return state.salesReports?.locations?.map((el) => ({
      ...el,
      items:
        el.items?.map((x) => ({
          date: x.date,
          sales: x.sales !== undefined ? x.sales : null,
          carCount: x.carCount !== undefined ? x.carCount : null,
          avgTicket: x.avgTicket !== undefined ? x.avgTicket : null,
          deferred: x.deferred !== undefined ? x.deferred : null
        })) ?? []
    }))
  }

  @Selector()
  static tiresReport(state: ReportsStateModel) {
    return state.tiresReport
  }

  @Selector()
  static compareReports(state: ReportsStateModel) {
    return state.compareReports
  }

  @Selector()
  static weeklyObjectivesRanking(state: ReportsStateModel) {
    return state.weeklyObjectivesRanking
  }

  @Selector()
  static monthlyObjectivesRanking(state: ReportsStateModel) {
    return state.monthlyObjectivesRanking
  }

  @Selector()
  static weeklyArMovingAverage(state: ReportsStateModel) {
    return state.weeklyArMovingAverage
  }

  @Selector()
  static monthlyArMovingAverage(state: ReportsStateModel) {
    return state.monthlyArMovingAverage
  }

  @Selector()
  static weeklyCategoryMovingAverage(state: ReportsStateModel) {
    return state.weeklyCategoryMovingAverage
  }

  @Selector()
  static monthlyCategoryMovingAverage(state: ReportsStateModel) {
    return state.monthlyCategoryMovingAverage
  }

  @Selector()
  static weeklyEmployeeEfficiencyRanking(state: ReportsStateModel) {
    return state.weeklyEmployeeEfficiencyRanking
  }

  @Selector()
  static monthlyEmployeeEfficiencyRanking(state: ReportsStateModel) {
    return state.monthlyEmployeeEfficiencyRanking
  }

  @Selector()
  static upsellReport(state: ReportsStateModel) {
    return state.upsellReport
  }

  @Selector()
  static upsellListResponse(state: ReportsStateModel) {
    return state.upsellListResponse
  }

  @Selector()
  static dailyAverageReport(state: ReportsStateModel) {
    return state.dailyAverageReport
  }

  @Selector()
  static compareObjectivesByGroup(state: ReportsStateModel): ICompareObjectivesByGroup | undefined {
    if (!state.compareObjectivesByGroup) {
      return
    }
    return {
      ...state.compareObjectivesByGroup,
      groupRankings: sortBy(state.compareObjectivesByGroup.groupRankings, (x) => x.orderNo)
    }
  }

  @Selector()
  static compareObjectivesByRegional(
    state: ReportsStateModel
  ): ICompareObjectivesByRegional | undefined {
    return state.compareObjectivesByRegional
  }

  @Selector()
  static adjustedGP(state: ReportsStateModel) {
    const calculateProgressValue = (item: IAdjustedGPInfo): IAdjustedGPInfo => {
      const progressToTier1 = (item.gpRealized / item.tier1) * 100
      const progressToTier2 = (item.gpRealized / item.tier2) * 100
      const progressToTier3 = (item.gpRealized / item.tier3) * 100

      let currentTier = 0
      let currentTierTextColor = 'text-gray-500'
      let currentTierLabel = 'Below target'
      let currentTierProgress = progressToTier1
      let currentTierProgressLabel = 'Progress vs. T1'
      let badgeTextColor = 'text-white'
      let currentTierBgColor = 'bg-gray-500'
      if (item.gpRealized >= item.tier3) {
        currentTier = 3
        currentTierLabel = 'Tier 3'
        currentTierProgress = progressToTier3
        currentTierProgressLabel = 'Progress vs. T3'
        badgeTextColor = 'text-txt'
        currentTierBgColor = 'bg-tiers-tier3'
        currentTierTextColor = 'text-tiers-tier3'
      } else if (item.gpRealized >= item.tier2) {
        currentTier = 2
        currentTierLabel = 'Tier 2'
        currentTierProgress = progressToTier3
        currentTierProgressLabel = 'Progress vs. T3'
        badgeTextColor = 'text-txt'
        currentTierBgColor = 'bg-tiers-tier2'
        currentTierTextColor = 'text-tiers-tier2'
      } else if (item.gpRealized >= item.tier1) {
        currentTier = 1
        currentTierLabel = 'Tier 1'
        currentTierProgress = progressToTier2
        currentTierProgressLabel = 'Progress vs. T2'
        badgeTextColor = 'text-white'
        currentTierBgColor = 'bg-tiers-tier1'
        currentTierTextColor = 'text-tiers-tier1'
      }

      return {
        ...item,
        currentTier,
        currentTierTextColor,
        currentTierLabel,
        currentTierProgress,
        currentTierProgressLabel,
        badgeTextColor,
        currentTierBgColor,
        progressToTier1,
        progressToTier2,
        progressToTier3
      }
    }

    const months: Record<number, IAdjustedGPMonth> = {}
    Object.values(state.adjustedGP ?? {}).forEach((el) => {
      months[el.monthNo] = {
        ...el,
        total: calculateProgressValue(el.total),
        locationData: el.locationData?.map((data) => calculateProgressValue(data))
      }
    })
    return months
  }

  @Action(GetCallsReport)
  getCallsReport(ctx: StateContext<ReportsStateModel>, { payload }: GetCallsReport) {
    ctx.patchState({
      callsReports: undefined
    })
    return this.reportsService.getCallsReport(payload).pipe(
      tap((res) => {
        ctx.patchState({
          callsReports: res
        })
      })
    )
  }

  @Action(GetSalesReport)
  getSalesReport(ctx: StateContext<ReportsStateModel>, { payload }: GetSalesReport) {
    ctx.patchState({
      salesReports: undefined
    })
    return this.reportsService.getSalesReport(payload).pipe(
      tap((res) => {
        ctx.patchState({
          salesReports: res
        })
      })
    )
  }

  @Action(GetTiresReport, { cancelUncompleted: true })
  getTiresReport(ctx: StateContext<ReportsStateModel>, { payload }: GetTiresReport) {
    ctx.patchState({
      tiresReport: undefined
    })
    return this.reportsService.getTiresReport(payload).pipe(
      tap((res) => {
        ctx.patchState({
          tiresReport: res
        })
      })
    )
  }

  @Action(GetCompareReport)
  getCompareReport(ctx: StateContext<ReportsStateModel>, { payload }: GetCompareReport) {
    ctx.patchState({
      compareReports: undefined
    })
    return this.reportsService.getCompareReport(payload).pipe(
      tap((res) => {
        ctx.patchState({
          compareReports: res
        })
      })
    )
  }

  @Action(GetWeeklyObjectivesRankingReport)
  getWeeklyObjectivesRankingReport(
    ctx: StateContext<ReportsStateModel>,
    { objectiveCategory }: GetWeeklyObjectivesRankingReport
  ) {
    ctx.patchState({
      weeklyObjectivesRanking: undefined
    })
    return this.reportsService.getWeeklyObjectivesRankingReport(objectiveCategory).pipe(
      tap((res) => {
        ctx.patchState({
          weeklyObjectivesRanking: res?.rankings ?? []
        })
      })
    )
  }

  @Action(GetMonthlyObjectivesRankingReport)
  getMonthlyObjectivesRankingReport(
    ctx: StateContext<ReportsStateModel>,
    { objectiveCategory }: GetMonthlyObjectivesRankingReport
  ) {
    ctx.patchState({
      monthlyObjectivesRanking: undefined
    })
    return this.reportsService.getMonthlyObjectivesRankingReport(objectiveCategory).pipe(
      tap((res) => {
        ctx.patchState({
          monthlyObjectivesRanking: res?.rankings ?? []
        })
      })
    )
  }

  @Action(GetWeeklyArMovingAverageReport)
  getWeeklyArMovingAverageReport(
    ctx: StateContext<ReportsStateModel>,
    { payload }: GetWeeklyArMovingAverageReport
  ) {
    ctx.patchState({
      weeklyArMovingAverage: undefined
    })
    return this.reportsService.getWeeklyArMovingAverageReport(payload).pipe(
      tap((res) => {
        ctx.patchState({
          weeklyArMovingAverage: res?.rankings ?? []
        })
      })
    )
  }

  @Action(GetMonthlyArMovingAverageReport)
  getMonthlyArMovingAverageReport(
    ctx: StateContext<ReportsStateModel>,
    { payload }: GetMonthlyArMovingAverageReport
  ) {
    ctx.patchState({
      monthlyArMovingAverage: undefined
    })
    return this.reportsService.getMonthlyArMovingAverageReport(payload).pipe(
      tap((res) => {
        ctx.patchState({
          monthlyArMovingAverage: res?.rankings ?? []
        })
      })
    )
  }

  @Action(GetWeeklyCategoryMovingAverageReport)
  getWeeklyCategoryMovingAverageReport(
    ctx: StateContext<ReportsStateModel>,
    { serviceCategoryId }: GetWeeklyCategoryMovingAverageReport
  ) {
    ctx.patchState({
      weeklyCategoryMovingAverage: undefined
    })
    return this.reportsService.getWeeklyCategoryMovingAverageReport(serviceCategoryId).pipe(
      tap((res) => {
        ctx.patchState({
          weeklyCategoryMovingAverage: res?.rankings ?? []
        })
      })
    )
  }

  @Action(GetMonthlyCategoryMovingAverageReport)
  getMonthlyCategoryMovingAverageReport(
    ctx: StateContext<ReportsStateModel>,
    { serviceCategoryId }: GetMonthlyCategoryMovingAverageReport
  ) {
    ctx.patchState({
      monthlyCategoryMovingAverage: undefined
    })
    return this.reportsService.getMonthlyCategoryMovingAverageReport(serviceCategoryId).pipe(
      tap((res) => {
        ctx.patchState({
          monthlyCategoryMovingAverage: res?.rankings ?? []
        })
      })
    )
  }

  @Action(GetWeeklyEmployeeEfficiencyRankingReport)
  getWeeklyEmployeeEfficiencyRankingReport(
    ctx: StateContext<ReportsStateModel>,
    { employeeEfficiencyType }: GetWeeklyEmployeeEfficiencyRankingReport
  ) {
    ctx.patchState({
      weeklyEmployeeEfficiencyRanking: undefined
    })
    return this.reportsService.getWeeklyEmployeeEfficiencyRankingReport(employeeEfficiencyType).pipe(
      tap((res) => {
        ctx.patchState({
          weeklyEmployeeEfficiencyRanking: res?.rankings ?? []
        })
      })
    )
  }

  @Action(GetMonthlyEmployeeEfficiencyRankingReport)
  getMonthlyEmployeeEfficiencyRankingReport(
    ctx: StateContext<ReportsStateModel>,
    { employeeEfficiencyType }: GetMonthlyEmployeeEfficiencyRankingReport
  ) {
    ctx.patchState({
      monthlyEmployeeEfficiencyRanking: undefined
    })
    return this.reportsService.getMonthlyEmployeeEfficiencyRankingReport(employeeEfficiencyType).pipe(
      tap((res) => {
        ctx.patchState({
          monthlyEmployeeEfficiencyRanking: res?.rankings ?? []
        })
      })
    )
  }

  @Action(GetUpsellReport)
  getUpsellReport(ctx: StateContext<ReportsStateModel>, { payload }: GetUpsellReport) {
    ctx.patchState({
      upsellReport: undefined
    })
    return this.reportsService.getUpsellReport(payload).pipe(
      tap((res) => {
        ctx.patchState({
          upsellReport: res
        })
      })
    )
  }

  @Action(GetUpsellList)
  getUpsellList(ctx: StateContext<ReportsStateModel>, { info }: GetUpsellList) {
    ctx.patchState({
      upsellListResponse: undefined
    })
    return this.reportsService.getUpsellList(info).pipe(
      tap((res) => {
        ctx.patchState({
          upsellListResponse: res
        })
      })
    )
  }

  @Action(GetDailyAverageReport)
  getDailyAverageReport(ctx: StateContext<ReportsStateModel>, { payload }: GetDailyAverageReport) {
    ctx.patchState({
      dailyAverageReport: undefined
    })
    return this.reportsService.getDailyAverageReport(payload).pipe(
      tap((res) => {
        ctx.patchState({
          dailyAverageReport: res
        })
      })
    )
  }

  @Action(GetCompareObjectivesByGroup)
  getCompareObjectivesByGroup(
    ctx: StateContext<ReportsStateModel>,
    { numberOfMonths, locationIds }: GetCompareObjectivesByGroup
  ) {
    ctx.patchState({
      compareObjectivesByGroup: undefined
    })
    return this.reportsService.getCompareObjectivesByGroup(numberOfMonths, locationIds).pipe(
      tap((res) => {
        ctx.patchState({
          compareObjectivesByGroup: res
        })
      })
    )
  }

  @Action(GetCompareObjectivesByRegional)
  getCompareObjectivesByRegional(
    ctx: StateContext<ReportsStateModel>,
    { numberOfMonths, locationIds }: GetCompareObjectivesByRegional
  ) {
    ctx.patchState({
      compareObjectivesByRegional: undefined
    })
    return this.reportsService.getCompareObjectivesByRegional(numberOfMonths, locationIds).pipe(
      tap((res) => {
        ctx.patchState({
          compareObjectivesByRegional: res
        })
      })
    )
  }

  @Action(GetAdjustedGP)
  getAdjustedGP(ctx: StateContext<ReportsStateModel>, { payload }: GetAdjustedGP) {
    return this.reportsService.getAdjustedGP(payload).pipe(
      tap((res) => {
        const month = res?.months?.[0]
        if (!month) {
          return
        }
        ctx.patchState({
          adjustedGP: { ...ctx.getState().adjustedGP, [month.monthNo]: month }
        })
      })
    )
  }

  @Action(ClearAdjustedGP)
  clearAdjustedGP(ctx: StateContext<ReportsStateModel>) {
    ctx.patchState({
      adjustedGP: {}
    })
  }
}
